import React, { useState } from "react";
import { Brain, Hand, MousePointerClick, Minus, StickyNote, FileText } from "lucide-react";

export default function Projects({ darkMode }) {
  const [selectedProject, setSelectedProject] = useState(null);

  const projects = [
    {
      title: "Zed.AI – Your AI Study Buddy",
      gradient: "from-purple-500 via-violet-400 to-purple-600",
      color: {
        light: "bg-purple-100 text-purple-700 border-purple-200",
        dark: "bg-purple-900 text-purple-300 border-purple-700",
      },
      icon: (
        <div className="p-2.5 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center shadow-sm">
          <Brain size={20} className="text-purple-500 dark:text-purple-300" />
        </div>
      ),
      about: `Zed.AI is your autonomous AI study partner that connects directly to your student account and responds to your voice to create a personalized and highly productive learning experience. It authenticates securely via Auth0, scrapes your actual course content, and uses Gemini 2.5 Computer-Use API to autonomously generate study plans, quizzes, and progress tracking.`,
      nextSteps: `We plan to expand Zed.AI to support more LMS platforms (like Blackboard), deeper personalization using learning analytics, and adaptive feedback based on your progress. We also want to bring mobile compatibility for on-the-go studying.`,
      link: "https://github.com/alextgu/emberhacks",
      devpost: "https://devpost.com/software/zed-7z0wg4",
      tech: "React, Tailwind CSS, Flask, Gemini 2.5, ElevenLabs, Auth0, Porcupine, Playwright",
      note: "HACKATHON WINNER",
    },
    {
      title: "Sign Language Interpreter",
      gradient: "from-blue-500 via-sky-400 to-blue-600",
      color: {
        light: "bg-blue-100 text-blue-700 border-blue-200",
        dark: "bg-blue-900 text-blue-300 border-blue-700",
      },
      icon: (
        <div className="p-2.5 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center shadow-sm">
          <Hand size={20} className="text-blue-500 dark:text-blue-300" />
        </div>
      ),
      about:
        "A real-time sign language detection app using Python, OpenCV, and MediaPipe to translate gestures into text.",
      nextSteps:
        "I'd love to expand the vocabulary to include more complex signs, multi-hand gestures, and support for different sign languages.",
      link: "https://github.com/sakets-dev/sign_language_detector",
      tech: "Python, OpenCV, MediaPipe, KNN",
    },
    {
      title: "Vision-Controlled Mouse",
      gradient: "from-green-500 via-emerald-400 to-green-600",
      color: {
        light: "bg-green-100 text-green-700 border-green-200",
        dark: "bg-green-900 text-green-300 border-green-700",
      },
      icon: (
        <div className="p-2.5 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center shadow-sm">
          <MousePointerClick size={20} className="text-green-500 dark:text-green-300" />
        </div>
      ),
      about:
        "A Python application that uses computer vision and hand tracking to control your mouse cursor with gestures in real time.",
      nextSteps:
        "Add gesture controls like right-click, scrolling, and drag-and-drop functionality.",
      link: "https://github.com/sakets-dev/vision_controlled_mouse",
      tech: "Python, OpenCV, MediaPipe, PyAutoGUI, NumPy",
    },
    {
      title: "Morse Code Decoder API",
      gradient: "from-yellow-500 via-amber-400 to-yellow-600",
      color: {
        light: "bg-yellow-100 text-yellow-800 border-yellow-200",
        dark: "bg-yellow-900 text-yellow-300 border-yellow-700",
      },
      icon: (
        <div className="p-2.5 rounded-lg bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center shadow-sm">
          <Minus size={20} className="text-yellow-500 dark:text-yellow-300" />
        </div>
      ),
      about:
        "A Flask-based API that encodes and decodes Morse code messages, complete with a web interface for instant testing.",
      nextSteps:
        "Add audio input/output capabilities and explore machine learning for automatic Morse code recognition.",
      link: "https://github.com/sakets-dev/morse-decoder-app",
      tech: "Python, Flask, HTML, CSS, JavaScript",
    },
    {
      title: "Sticky Note Chrome Extension",
      gradient: "from-pink-500 via-rose-400 to-pink-600",
      color: {
        light: "bg-pink-100 text-pink-700 border-pink-200",
        dark: "bg-pink-900 text-pink-300 border-pink-700",
      },
      icon: (
        <div className="p-2.5 rounded-lg bg-pink-100 dark:bg-pink-900 flex items-center justify-center shadow-sm">
          <StickyNote size={20} className="text-pink-500 dark:text-pink-300" />
        </div>
      ),
      about:
        "A Chrome extension that lets users create and save sticky notes on any webpage, with persistent storage across sessions.",
      nextSteps:
        "Add cloud sync, collaborative editing, and integration with tools like Notion or Google Keep.",
      link: "https://github.com/sakets-dev/sticky-note-extension",
      tech: "HTML, CSS, JavaScript, Chrome APIs",
    },
    {
      title: "Textify — PDF & Image to Text Converter",
      gradient: "from-red-500 via-rose-400 to-red-600",
      color: {
        light: "bg-red-100 text-red-700 border-red-200",
        dark: "bg-red-900 text-red-300 border-red-700",
      },
      icon: (
        <div className="p-2.5 rounded-lg bg-red-100 dark:bg-red-900 flex items-center justify-center shadow-sm">
          <FileText size={20} className="text-red-500 dark:text-red-300" />
        </div>
      ),
      about:
        "Textify is a full-stack web app that converts PDFs or images into editable text using OCR technology. It integrates a Flask backend for processing and a lightweight frontend hosted on GitHub Pages.",
      nextSteps:
        "Future updates will add support for batch conversion, drag-and-drop uploads, and multilingual OCR recognition.",
      link: "https://github.com/sakets-dev/converter_text",
      tech: "Python, Flask, PDFPlumber, pytesseract, HTML, CSS, JavaScript",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col items-center px-6 py-16 transition-colors duration-[1000ms] ease-in-out">
      {/* HEADER + GRID WRAPPER */}
      <div
        className={`transition-all duration-500 ease-out ${
          selectedProject ? "blur-lg opacity-50" : "blur-0 opacity-100"
        }`}
      >
        {/* HEADER */}
        <div
          className={`rounded-3xl w-full max-w-6xl text-center mb-10 p-8 border shadow-soft transition-all duration-500 ease-in-out hover:shadow-[0_0_25px_rgba(255,255,255,0.15)] ${
            darkMode
              ? "bg-[#202023] border-[#2a2a2d] text-gray-100"
              : "bg-[#f1f2f4] border-[#d8d8d8] text-gray-800"
          }`}
        >
          <h1 className="text-4xl font-bold mb-4">Projects</h1>
          <p
            className={`text-base leading-relaxed ${
              darkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Want to explore more of my work? Check out my{" "}
            <a
              href="https://github.com/sakets-dev"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:underline"
            >
              GitHub
            </a>
            . Below are a few of my featured projects that showcase my creativity and
            problem-solving approach.
          </p>
        </div>

        {/* PROJECT GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl w-full">
          {projects.map((p, i) => (
            <div
              key={i}
              onClick={() => setSelectedProject(p)}
              className={`relative cursor-pointer rounded-3xl p-6 border shadow-soft transition-all duration-200 ease-out hover:scale-[1.03] hover:shadow-xl hover:-translate-y-1 ${
                darkMode
                  ? "bg-[#1f1f22] border-[#2a2a2d] text-gray-100 hover:border-blue-400/40"
                  : "bg-white border-gray-200 text-gray-800 hover:border-blue-400/40"
              }`}
            >
              {p.note && (
                <div className="absolute top-0 left-0 w-full rounded-t-3xl bg-gradient-to-r from-yellow-400 via-yellow-300 to-amber-400 bg-[length:300%_300%] animate-gradientFlow text-black dark:text-gray-900 text-xs font-semibold py-2 text-center tracking-wide shadow-md">
                  {p.note}
                </div>
              )}

              <div className="mt-6 flex items-center gap-3 mb-3">
                {p.icon}
                <h2 className="text-lg font-semibold hover:text-blue-400 transition-colors duration-150">
                  {p.title}
                </h2>
              </div>

              <p
                className={`text-sm mb-3 leading-relaxed ${
                  darkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                {p.about.slice(0, 120)}...
              </p>

              <div className="flex flex-wrap gap-2 mt-3">
                {p.tech.split(",").map((t, idx) => (
                  <span
                    key={idx}
                    className={`px-2.5 py-[3px] text-xs font-medium rounded-md border transition-all duration-200 hover:scale-[1.05] ${
                      darkMode ? p.color.dark : p.color.light
                    }`}
                  >
                    {t.trim()}
                  </span>
                ))}
              </div>
            </div>
          ))}

          {/* Placeholder */}
          <div
            className={`rounded-3xl p-6 border border-dashed flex items-center justify-center text-center italic transition-all duration-[600ms] ease-in-out ${
              darkMode
                ? "bg-[#202023] border-[#2a2a2d] text-gray-400"
                : "bg-[#f1f2f4] border-[#d8d8d8] text-gray-600"
            }`}
          >
            New projects are in the works...
          </div>
        </div>
      </div>

      {/* MODAL */}
      {selectedProject && (
        <div
          className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-6 backdrop-blur-md animate-fadeIn"
          onClick={() => setSelectedProject(null)}
        >
          <div
            className={`rounded-3xl max-w-3xl w-full p-10 relative max-h-[90vh] overflow-y-auto shadow-2xl transform transition-all duration-300 animate-scaleIn ${
              darkMode ? "bg-[#1d1d20] text-gray-100" : "bg-white text-gray-900"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`absolute top-0 left-0 w-full h-1.5 rounded-t-3xl bg-gradient-to-r ${selectedProject.gradient} bg-[length:200%_200%] animate-gradientFlow`}
            />

            <div className="flex items-center gap-3 mb-5 mt-2">
              {selectedProject.icon}
              <h2 className="text-2xl font-semibold tracking-tight">
                {selectedProject.title}
              </h2>
            </div>

            <div
              className="space-y-3 text-[15px] leading-relaxed font-[system-ui]"
              style={{ letterSpacing: "-0.01em" }}
            >
              <p className={darkMode ? "text-gray-300" : "text-gray-700"}>
                {selectedProject.about}
              </p>
              <p className={darkMode ? "text-gray-300" : "text-gray-700"}>
                {selectedProject.nextSteps}
              </p>

              <div className="flex gap-3 pt-2">
                <a
                  href={selectedProject.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 rounded-md text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-500 hover:opacity-90 transition-all"
                >
                  View on GitHub
                </a>
                {selectedProject.devpost && (
                  <a
                    href={selectedProject.devpost}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 rounded-md text-sm font-medium text-white bg-gradient-to-r from-pink-500 to-rose-500 hover:opacity-90 transition-all"
                  >
                    View on Devpost
                  </a>
                )}
              </div>

              <div className="border-t pt-3 text-xs opacity-80 mt-3">
                <p>
                  <span className="font-medium">Built with:</span>{" "}
                  {selectedProject.tech}
                </p>
              </div>
            </div>

            <div className="mt-8 pt-5 border-t">
              <button
                onClick={() => setSelectedProject(null)}
                className={`w-full py-2.5 px-6 rounded-lg text-sm font-medium transition-all hover:scale-[1.02] ${
                  darkMode
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
